import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionVariantEnum,
} from "../../core/Accordion/Accordion";
import { BodyTyp } from "../../Typography/Typography";
import {
  CollaboratorInfo,
  FontWeightEnum,
  IconSizeEnum,
} from "../../../types/AppTypes";
import { IconManager } from "../../core/IconManager/IconManager";
import { ReactComponent as UsersCheckIcon } from "../../../icons/usersCheck.svg";
import { EmptyState } from "../../EmptyState/EmptyState";
import professionalDevelopment from "../../../images/professionalDevelopment.png";
import {
  EXAMINED_LABEL,
  NAME_SURNAME_EMAIL_LABEL,
  NO_COLLABORATORS_EXAMINED_MESSAGE,
} from "../../../constants/Dictionary";
import {
  StyledAccordionWrapper,
  StyledCollaboratorContainer,
  StyledCollaboratorExaminedContainer,
  StyledCollaboratorExaminedList,
  StyledInfoCollaboratorContainer,
  StyledSingleCollaboratorContainer,
  StyledTitleInternalGrade,
} from "./CollaboratorsExamined.styled";
import SearchInput from "../../core/SearchInput/SearchInput";
import { Loader } from "../../core/Loader/Loader";
import { theme } from "../../../constants/StyledComponents";
import { getInitials } from "../../../utils/collaborators.utils";
import { Avatar, AvatarDimensionsEnum } from "../../core/Avatar/Avatar";
import ToggleSwitch from "../../core/ToggleSwitch/ToggleSwitch";
import { InfiniteScrollList } from "../../InfiniteScrollList/InfiniteScrollList";
import { ReactComponent as RestoreIcon } from "../../../icons/restore2.svg";
import { PotentialMatrixFilters } from "../../../hooks/usePotentialMatrixFilters.hook";
import { PotentialMatrixFilterKeyEnum } from "../../../types/api/filters.type";
import {
  CollaboratorCalibration,
  InternalGradeStats,
} from "../../../types/api/potentialMatrix.type";
import { Pagination } from "../../../types/api/generic.type";
import { PendingCalibration } from "../CalibrationModal";

type CollaboratorsExaminedProps = {
  isEvaluationOpen: boolean;
  onExaminedOpen: (value: boolean) => void;
  hiddenCollaborators: number[];
  setHiddenCollaborators: (value: number[]) => void;
  filters: PotentialMatrixFilters;
  setSelectedCollaborator: (x: CollaboratorCalibration | null) => void;
  selectedCollaborator: CollaboratorCalibration | null;
  setOpenModalCollaboratorExaminated: (x: boolean) => void;
  setIsEvaluationOpen: (x: boolean) => void;
  handleFilters: (
    key: PotentialMatrixFilterKeyEnum,
    value: PotentialMatrixFilters[PotentialMatrixFilterKeyEnum],
  ) => void;
  setSelectedCareerManager: (x: CollaboratorInfo | null) => void;
  examinedCollaboratorsPage: number;
  fetchingMoreExaminedCollaborators: boolean;
  examinedCollaboratorsSearchValue: string;
  setExaminedCollaboratorsSearchValue: (x: string) => void;
  clearExaminedCollaboratorsSearch: () => void;
  fetchMoreExaminedCollaborators: () => void;
  loadMoreExaminedCollaborators: boolean;
  examinedCollaboratorsInitLoading: boolean;
  examinedCollaborators: CollaboratorCalibration[];
  examinedCollaboratorsPagination: Pagination | null;
  setPendingCalibration: (x: PendingCalibration | null) => void;
  potentialMatrixStatsData: InternalGradeStats[];
  loadingPotentialMatrixStats: boolean;
};

export const CollaboratorsExamined = ({
  isEvaluationOpen,
  onExaminedOpen,
  hiddenCollaborators,
  setHiddenCollaborators,
  setSelectedCollaborator,
  selectedCollaborator,
  setOpenModalCollaboratorExaminated,
  setIsEvaluationOpen,
  handleFilters,
  setSelectedCareerManager,
  fetchingMoreExaminedCollaborators,
  examinedCollaboratorsSearchValue,
  setExaminedCollaboratorsSearchValue,
  clearExaminedCollaboratorsSearch,
  fetchMoreExaminedCollaborators,
  loadMoreExaminedCollaborators,
  examinedCollaboratorsInitLoading,
  examinedCollaborators,
  examinedCollaboratorsPagination,
  setPendingCalibration,
  potentialMatrixStatsData,
  loadingPotentialMatrixStats,
}: CollaboratorsExaminedProps) => {
  const [hasData, setHasData] = useState<boolean | null>(null);

  useEffect(() => {
    if (hasData === null && examinedCollaboratorsPagination) {
      const thereIsData = examinedCollaboratorsPagination?.total > 0;
      setHasData(thereIsData);
    }
  }, [examinedCollaborators, hasData]);

  const getInternalGradeStats = (internalGrade: string | null) => {
    if (!internalGrade) {
      return null;
    }

    const internalGradeStats = potentialMatrixStatsData?.find(
      (item) => item.internalGradeEnum === internalGrade,
    );

    return internalGradeStats || null;
  };

  const handleHiddenCollaboratorsChange = (item: CollaboratorCalibration) => {
    setHiddenCollaborators(
      hiddenCollaborators.includes(item.collaborator.id)
        ? hiddenCollaborators.filter((id) => id !== item.collaborator.id)
        : [...hiddenCollaborators, item.collaborator.id],
    );
  };

  const handleSelectCollaborator = (item: CollaboratorCalibration) => {
    if (hiddenCollaborators.includes(item.collaborator.id)) {
      return;
    }

    setOpenModalCollaboratorExaminated(true);
    setIsEvaluationOpen(false);
    setSelectedCareerManager(null);

    handleFilters(PotentialMatrixFilterKeyEnum.INTERNAL_GRADE, ["all"]);

    setSelectedCollaborator(
      selectedCollaborator?.collaborator.id === item.collaborator.id
        ? null
        : item,
    );
    setPendingCalibration({
      finalDimensionId: item.calibration.finalDimensionId,
      finalScreen: item.calibration.finalScreen,
      finalDimensionPercentage: item.calibration.finalDimensionPercentage,
      finalComment: item.calibration.finalComment ?? null,
    });
  };

  const showSearch = examinedCollaboratorsInitLoading || hasData;

  return (
    <StyledAccordionWrapper>
      <Accordion
        label={
          <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD}>
            {EXAMINED_LABEL}
          </BodyTyp>
        }
        variant={AccordionVariantEnum.SECONDARY}
        leftComponent={
          <IconManager size={IconSizeEnum.SMALL}>
            <UsersCheckIcon />
          </IconManager>
        }
        onToggle={onExaminedOpen}
        toScroll
      >
        <StyledCollaboratorContainer $isEvaluationOpen={isEvaluationOpen}>
          {showSearch ? (
            <SearchInput
              id="search"
              label={NAME_SURNAME_EMAIL_LABEL}
              name="search"
              onChange={setExaminedCollaboratorsSearchValue}
              value={examinedCollaboratorsSearchValue}
              onClear={clearExaminedCollaboratorsSearch}
            />
          ) : null}

          {examinedCollaborators.length > 0 ||
          examinedCollaboratorsInitLoading ? (
            <StyledCollaboratorExaminedList
              $isEvaluationOpen={isEvaluationOpen}
            >
              <InfiniteScrollList<CollaboratorCalibration>
                items={examinedCollaborators}
                gapElementList={0}
                renderItem={(item, index) => {
                  const currentGrade = item.collaborator.internalGrade;
                  const previousItem = examinedCollaborators[index - 1];
                  const previousGrade =
                    previousItem?.collaborator.internalGrade;

                  const showHeader =
                    index === 0 || currentGrade !== previousGrade;

                  const stats = getInternalGradeStats(currentGrade);

                  return (
                    <>
                      {showHeader && (
                        <StyledCollaboratorExaminedContainer>
                          <StyledTitleInternalGrade>
                            <BodyTyp
                              variant="2"
                              fontWeight={FontWeightEnum.SEMIBOLD}
                            >
                              {currentGrade}
                            </BodyTyp>
                            {loadingPotentialMatrixStats ? (
                              <Loader loading size={12} />
                            ) : (
                              <BodyTyp
                                variant="2"
                                fontWeight={FontWeightEnum.SEMIBOLD}
                              >
                                {stats?.examinedCount}
                              </BodyTyp>
                            )}
                          </StyledTitleInternalGrade>
                        </StyledCollaboratorExaminedContainer>
                      )}
                      <StyledSingleCollaboratorContainer
                        key={item.collaborator.id}
                        $isSelectedCollaborator={
                          selectedCollaborator?.collaborator.id ===
                          item.collaborator.id
                        }
                      >
                        <StyledInfoCollaboratorContainer
                          $disabled={
                            hiddenCollaborators.includes(
                              item.collaborator.id,
                            ) || item?.cm === null
                          }
                          onClick={() =>
                            item?.cm === null
                              ? {}
                              : selectedCollaborator?.collaborator.id ===
                                  item.collaborator.id
                                ? null
                                : handleSelectCollaborator(item)
                          }
                        >
                          <Avatar
                            collaboratorId={item.collaborator.id}
                            initials={getInitials(
                              item.collaborator.name,
                              item.collaborator.surname,
                            )}
                            variant={AvatarDimensionsEnum.SMALL}
                          />
                          <div>
                            <BodyTyp
                              variant="2"
                              fontWeight={FontWeightEnum.MEDIUM}
                            >
                              {item.collaborator.surname}{" "}
                              {item.collaborator.name}
                            </BodyTyp>
                            {!hiddenCollaborators.includes(
                              item.collaborator.id,
                            ) ? (
                              <BodyTyp
                                variant="3"
                                fontWeight={FontWeightEnum.SEMIBOLD}
                                color={theme.colors.grey_2}
                              >
                                {`${item.calibration.finalScreen} | ${item.calibration.finalDimensionPercentage}%`}
                              </BodyTyp>
                            ) : null}
                          </div>
                        </StyledInfoCollaboratorContainer>
                        {selectedCollaborator?.collaborator.id ===
                        item.collaborator.id ? (
                          <IconManager
                            size={IconSizeEnum.MEDIUM}
                            color={theme.colors.purple}
                          >
                            <RestoreIcon />
                          </IconManager>
                        ) : (
                          <ToggleSwitch
                            value={
                              !hiddenCollaborators.includes(
                                item.collaborator.id,
                              )
                            }
                            onChange={() =>
                              handleHiddenCollaboratorsChange(item)
                            }
                          />
                        )}
                      </StyledSingleCollaboratorContainer>
                    </>
                  );
                }}
                onScroll={fetchMoreExaminedCollaborators}
                canLoadMore={loadMoreExaminedCollaborators}
                loading={examinedCollaboratorsInitLoading}
                fetchingMore={fetchingMoreExaminedCollaborators}
              />
            </StyledCollaboratorExaminedList>
          ) : (
            <div className="empty-state-container">
              <EmptyState
                imageSrc={professionalDevelopment}
                label={NO_COLLABORATORS_EXAMINED_MESSAGE}
                maxWidth={170}
              />
            </div>
          )}
        </StyledCollaboratorContainer>
      </Accordion>
    </StyledAccordionWrapper>
  );
};
